import React from 'react';
import {
  Box, HStack, IconButton, Text, Tooltip, useToast,
} from '@chakra-ui/react';
import { FiEye, FiDownload } from 'react-icons/fi';
import IconWorkOrder from '../assets/Icon-WorkOrder.svg';
import DocumentGridShell from './DocumentGridShell';
import LedgerTable, { LedgerColumn } from './LedgerTable';
import LedgerSubline from './LedgerSubline';
import StatusPill from './StatusPill';
import openPdf, { previewPdf } from '../utils/openPdf';
import { Proposal, ProposalStatus } from '../models/proposal';

// item 461/553 — HAND-OFFS: once a proposal is approved, its hand-off document (the
// numbers-free deployment brief, item 34/423) shows here so the field/deployment team can
// open it — they can't open proposals at all, but the project page is theirs. item 553 moved
// this off its bespoke tree markup onto the shared LEDGER grammar (DocumentGridShell +
// LedgerTable) so it reads like every other project section.
export const APPROVED_ERA = [
  ProposalStatus.Approved, ProposalStatus.ApprovedDeposited, ProposalStatus.Complete,
];

// mirror the proposals ledger: ApprovedDeposited reads as "In Progress" on the pill
const pillLabel = (s: ProposalStatus): string => (
  s === ProposalStatus.ApprovedDeposited ? 'In Progress' : ProposalStatus[s]
);

const ProjectHandOffsSection: React.FC<{
  proposals?: Proposal[];
  collapse?: { collapsed: boolean; onToggle: () => void };
}> = ({ proposals, collapse }) => {
  const toast = useToast();
  const approved = (proposals ?? []).filter(
    (p) => !p.isVoid && APPROVED_ERA.includes(p.status),
  );

  const onErr = () => toast({
    status: 'error', title: 'Could not open the hand-off', duration: 6000, isClosable: true,
  });
  // item 701 (Chad) — preview the hand-off in-browser (eyeball); Download still available.
  const preview = (id: string) => { previewPdf(`proposal/${id}/handoff-pdf`).catch(onErr); };
  const download = (id: string) => { openPdf(`proposal/${id}/handoff-pdf`).catch(onErr); };

  const columns: LedgerColumn<Proposal>[] = [
    {
      // item 1445 (Chad: "a pass on all the ledgers … the name at the bottom under the other items like the portal") —
      // PROPOSAL = number bold, title as two-line subtext, the hand-off note beneath.
      // item 1447 — title + the hand-off note ride the full-width rowSubline; number here.
      key: 'name',
      header: 'PROPOSAL',
      width: '150px',
      sortValue: (p) => p.proposalNumber ?? p.proposalTitle ?? '',
      render: (p) => <Text fontWeight="semibold" whiteSpace="nowrap">{p.proposalNumber || '—'}</Text>,
    },
    {
      key: 'status',
      header: 'STATUS',
      width: '160px',
      sortValue: (p) => p.status,
      render: (p) => <StatusPill label={pillLabel(p.status)} minW="118px" />,
    },
  ];

  const rowActions = (p: Proposal): React.ReactNode => (
    <HStack spacing={1} justify="flex-end">
      {/* item 701 — compact icon buttons (matching the other doc grids); text buttons overflowed the column */}
      <Tooltip label="Preview" openDelay={300}>
        <IconButton size="xs" variant="ghost" aria-label="Preview" icon={<FiEye />} onClick={() => preview(p.id)} />
      </Tooltip>
      <Tooltip label="Download PDF" openDelay={300}>
        <IconButton size="xs" variant="ghost" aria-label="Download PDF" icon={<FiDownload />} onClick={() => download(p.id)} />
      </Tooltip>
    </HStack>
  );

  return (
    <DocumentGridShell
      heading="HAND-OFFS"
      icon={IconWorkOrder}
      collapsible={!!collapse}
      collapsed={collapse?.collapsed}
      onToggleCollapse={collapse?.onToggle}
      status="success"
      data={approved}
    >
      <LedgerTable<Proposal>
        rows={approved}
        columns={columns}
        rowSubline={(p) => <LedgerSubline name={p.proposalTitle} meta={['Hand-off document', p.handOffNotes ? 'has deployment notes' : null]} />}
        rowId={(p) => p.id}
        rowActions={rowActions}
        rowActionsWidth="120px"
        rowActionsHeader="ACTION"
        selectable={false}
        searchText={(p) => `${p.proposalNumber ?? ''} ${p.proposalTitle ?? ''}`}
        searchPlaceholder="Search hand-offs…"
        emptyText="No hand-offs yet — a proposal's hand-off appears here once it's approved."
      />
    </DocumentGridShell>
  );
};

ProjectHandOffsSection.defaultProps = { proposals: undefined, collapse: undefined };

export default ProjectHandOffsSection;
